/**
 * The crew screen — list, invite, remove (§5, §6). Owner only.
 *
 * "Owner only" here is belt and braces on purpose. The route is gated before
 * this mounts, and `GET /garages/:id/members` is itself owner-only server-side
 * (a crew member calling it gets a 404, not a 403 — anything they may not see
 * is reported as absent, §11). This component still checks, because a screen
 * that renders an invite form for someone who cannot invite is a bug even if
 * the API stops it.
 */

import { useState } from 'react';
import { Mail, ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react';

import { useInviteMember, useMembers, useRemoveMember } from '../api/hooks';
import { ApiError } from '../api/client';
import { Empty, ErrorState, Loading, errorMessage } from '../components/states';
import { Eyebrow } from '../components/primitives';
import { ShareToggle } from './ShareToggle';
import { useGarageContext, usePermissions } from './GarageProvider';

export function Members() {
  const { garage } = useGarageContext();
  const { canManageMembers } = usePermissions();

  if (!garage) return <Loading />;

  if (!canManageMembers) {
    return (
      <div className="screen">
        <Empty
          icon={<ShieldCheck size={20} />}
          title="Crew only see their own garage"
          hint="Only the garage owner can manage crew and sharing."
        />
      </div>
    );
  }

  return (
    <div className="screen">
      <Eyebrow>Share &amp; crew</Eyebrow>
      <h1 className="h1">{garage.name}</h1>
      <p className="lede">
        Make this garage public with a read-only link, and manage who can help run it. Crew can log service, mark
        inspections checked and record events — they <b className="c-ink">cannot</b> add or remove cars, drivers or
        parts, manage crew, or change sharing.
      </p>

      <div className="row-h">
        <h2 className="h2">Public sharing</h2>
      </div>
      <ShareToggle garage={garage} />

      <div className="row-h">
        <h2 className="h2">Crew</h2>
      </div>
      <MemberList garageId={garage.id} ownerLabel={garage.name} />
    </div>
  );
}

function MemberList({ garageId }: { garageId: string; ownerLabel: string }) {
  const members = useMembers(garageId);
  const remove = useRemoveMember(garageId);
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

  return (
    <>
      <InviteForm garageId={garageId} />

      {members.isPending && <Loading label="Loading crew…" />}
      {members.isError && <ErrorState error={members.error} onRetry={() => void members.refetch()} />}

      {members.data && members.data.length === 0 && (
        <Empty icon={<Users size={20} />} title="No crew yet" hint="Invite someone by the email they sign in with." />
      )}

      {members.data && members.data.length > 0 && (
        <div className="member-list">
          {members.data.map((m) => (
            <div className="member-row" key={m.userId}>
              <span className="member-ic">{m.name.slice(0, 1).toUpperCase()}</span>
              <div className="member-id">
                <b>{m.name}</b>
                <span>{m.email}</span>
              </div>
              <span className={'gsw-role role-' + m.role}>{m.role}</span>

              {/**
               * No remove button for the owner. The API refuses it outright —
               * removing the owner would orphan the garage, leaving nobody able
               * to manage crew or revoke the link (routes/garages.ts) — so
               * offering the button would only ever produce a 403.
               */}
              {m.role === 'owner' ? (
                <span className="member-protected" title="The owner cannot be removed — delete the garage instead">
                  owner
                </span>
              ) : pendingRemoval === m.userId ? (
                <span className="member-confirm">
                  <button className="act act-ghost" onClick={() => setPendingRemoval(null)}>
                    Cancel
                  </button>
                  <button
                    className="act act-danger"
                    disabled={remove.isPending}
                    onClick={() => {
                      remove.mutate(m.userId);
                      setPendingRemoval(null);
                    }}
                  >
                    Remove
                  </button>
                </span>
              ) : (
                <button className="act act-ghost" aria-label={`Remove ${m.name}`} onClick={() => setPendingRemoval(m.userId)}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {remove.isError && <div className="field-err">{errorMessage(remove.error)}</div>}
    </>
  );
}

function InviteForm({ garageId }: { garageId: string }) {
  const [email, setEmail] = useState('');
  const invite = useInviteMember(garageId);

  const submit = () => {
    if (!email.trim() || invite.isPending) return;
    invite.mutate({ email: email.trim(), role: 'crew' }, { onSuccess: () => setEmail('') });
  };

  /**
   * The 404 case deserves its own words.
   *
   * Invite-by-email only reaches people who already have an account: the API
   * will not mint a shell user from an address an owner typed, because whoever
   * later signed in with it would silently inherit the membership
   * (routes/garages.ts). So a 404 here is not "something is broken", it is
   * "they have not signed in yet" — and the owner needs to know that is the
   * fix, not retry.
   */
  const notFound = invite.error instanceof ApiError && invite.error.code === 'not_found';

  return (
    <div className="invite">
      <div className="invite-row">
        <span className="invite-ic">
          <Mail size={14} />
        </span>
        <input
          className="input"
          type="email"
          value={email}
          placeholder="crew@example.com"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          aria-label="Invite crew by email"
        />
        <button className="act" onClick={submit} disabled={!email.trim() || invite.isPending}>
          <UserPlus size={12} /> {invite.isPending ? 'Inviting…' : 'Invite crew'}
        </button>
      </div>

      {notFound ? (
        <div className="field-err">
          No SvcDue account uses that email yet. Ask them to sign in with Google once, then invite them.
        </div>
      ) : (
        invite.isError && <div className="field-err">{errorMessage(invite.error)}</div>
      )}

      <div className="invite-note">Crew join as crew. A garage has exactly one owner.</div>
    </div>
  );
}
